import React, { useEffect, useState } from 'react';
import { ColumnsType } from 'antd/es/table';
import { productUnit, initProductUnit } from '../../../model/master';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { api_product_unit } from '../../../api/product';
import CommonTableList from '../../common/common-table-list';
import CommonDetail from '../../common/common-detail';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { Modal } from 'antd';
import { isEmpty } from '../../../utils/util';
import ProductUnitDetail from './product-unit-detail';
import { printProfileCard } from '../../../utils/record-print-utils';
import { deleteFailureMessage } from '../../../utils/write-result';

const ProductUnit: React.FC = () => {
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState('');
    const [title, setTitle] = useState('');
    const [data, setData] = useState<productUnit[]>([]);
    const [formData, setFormData] = useState<productUnit>(initProductUnit);

    const filterField: any = ['name', 'symbol', 'remark'];
    const placeholder_txt: string = 'ค้นหาตามชื่อหน่วยสินค้า / สัญลักษณ์...';

    const onPrintProductUnit = (record: any) => {
        printProfileCard('ข้อมูลหน่วยสินค้า (PRODUCT UNIT)', [
            { label: 'รหัสหน่วย', value: record.code || `UNIT-${record.id}` },
            { label: 'ชื่อหน่วยสินค้า', value: record.name },
            { label: 'สัญลักษณ์ / ตัวย่อ', value: record.symbol || '-' },
            { label: 'จำนวนสินค้าที่ใช้หน่วยนี้', value: `${record.products_count || 0} รายการ` },
            { label: 'คำอธิบาย / หมายเหตุ', value: record.remark || '-' }
        ]);
    };

    const columns: ColumnsType<productUnit> = [
        {
            title: 'ลำดับ',
            dataIndex: 'key',
            key: 'key',
            width: '70px',
            align: 'center',
        },
        { 
            title: 'ชื่อหน่วยสินค้า', 
            dataIndex: 'name', 
            key: 'name',
            render: (text) => (
                <div style={{display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600}}>
                    <span style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        background: 'var(--secondary-light, #ECFDF5)',
                        color: 'var(--secondary, #10B981)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px'
                    }}>
                        ⚖️
                    </span>
                    <span>{text}</span>
                </div>
            )
        },
        { 
            title: 'สัญลักษณ์ / ตัวย่อ', 
            dataIndex: 'symbol', 
            key: 'symbol',
            width: '150px',
            render: (symbol) => (
                symbol ? (
                    <span style={{
                        background: 'var(--primary-light, #FFF3EE)',
                        color: 'var(--primary, #FF7A59)',
                        padding: '3px 10px',
                        borderRadius: '8px',
                        fontWeight: 700,
                        fontSize: '13px',
                        fontFamily: 'Nunito, sans-serif'
                    }}>
                        {symbol}
                    </span>
                ) : <span style={{color: '#94A3B8'}}>-</span>
            )
        },
        { 
            title: 'จำนวนสินค้าที่ใช้', 
            dataIndex: 'products_count', 
            key: 'products_count',
            width: '150px',
            align: 'center',
            render: (count) => (
                <span style={{
                    background: 'var(--bg-subtle, #F1F5F9)',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontWeight: 700,
                    fontSize: '13.5px',
                    color: 'var(--text-primary, #1E293B)',
                    fontFamily: 'Nunito, sans-serif'
                }}>
                    {count || 0} รายการ
                </span>
            )
        },
        { 
            title: 'คำอธิบาย / หมายเหตุ', 
            dataIndex: 'remark', 
            key: 'remark',
            render: (text) => text || <span style={{color: '#94A3B8'}}>-</span>
        },
        { 
            title: 'วันที่สร้าง', 
            dataIndex: 'create_date', 
            key: 'create_date',
            width: '160px',
            render: (text) => text || <span style={{color: '#94A3B8'}}>-</span>
        },
        {
            title: 'สถานะ',
            dataIndex: 'status',
            key: 'status',
            width: '130px',
            align: 'center',
            render: (status) => (
                <span style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '12.5px',
                    fontWeight: 700,
                    background: status === 1 ? '#ECFDF5' : '#F1F5F9',
                    color: status === 1 ? '#059669' : '#64748B',
                    border: `1px solid ${status === 1 ? '#A7F3D0' : '#E2E8F0'}`
                }}>
                    {status === 1 ? '🟢 ใช้งานอยู่' : '⚪ ปิดใช้งาน'}
                </span>
            )
        },
        { 
            title: 'การดำเนินการ', 
            dataIndex: 'operation', 
            width: '180px', 
            fixed: 'right', 
            render: (_, record) =>
                data.length >= 1 ? ( 
                    <div className='iconOperation'>
                        <FontAwesomeIcon icon="eye" title="ดูรายละเอียด" onClick={() => { onShowDetail('VIEW', record); }} />
                        <FontAwesomeIcon icon="print" title="พิมพ์หน่วยสินค้า" onClick={() => { onPrintProductUnit(record); }} />
                        <FontAwesomeIcon icon="pen-to-square" title="แก้ไข" onClick={() => { onShowDetail('EDIT', record); }} />
                        <FontAwesomeIcon icon="trash-can" title="ลบ" onClick={() => { onDelete(record.id, record.products_count); }} />
                    </div>
                ) : null
        }
    ];

    const init = async () => {
        let getProductUnit = await api_product_unit('LIST', {});
        setData(getProductUnit || []);
    };

    const getConfigModal = (titleStr: string, contentStr: string) => {
        return {
            title: titleStr,
            icon: <ExclamationCircleFilled rev={undefined} />,
            content: contentStr,
            okText: 'ตกลง',
            cancelText: 'ยกเลิก',
            centered: true
        };
    };

    const onShowDetail = (m: string, itemData?: productUnit) => {
        if (m === 'ADD') {
            setTitle('เพิ่มหน่วยสินค้า');
            setFormData({...initProductUnit});
        } else if (m === 'COPY') {
            setTitle('คัดลอกหน่วยสินค้า');
            setFormData({...(itemData || initProductUnit), id: 0, key: 0, name: `${itemData?.name || ''} (คัดลอก)`});
        } else if (m === 'EDIT') {
            setTitle('แก้ไขหน่วยสินค้า');
            setFormData(itemData ? {...itemData} : {...initProductUnit});
        } else if (m === 'VIEW') {
            setTitle('รายละเอียดหน่วยสินค้า');
            setFormData(itemData ? {...itemData} : {...initProductUnit});
        }

        setMode(m);
        setOpen(true);
    };

    const onCloseDetail = () => {
        setOpen(false);
    };

    const onSave = async () => {
        let operation = 'INSERT';
        let key_id = undefined;
        let body = JSON.parse(JSON.stringify(formData));

        if (formData.id > 0 && mode === 'EDIT') {
            operation = 'UPDATE';
            key_id = formData.id;
        }

        if (!isEmpty(body['name'])) {
            let res = await api_product_unit(operation, body, key_id);
            if (res === 1) {
                setOpen(false);
                Modal.success({
                    ...getConfigModal('สำเร็จ', operation === 'UPDATE' ? 'แก้ไขข้อมูลสำเร็จแล้ว' : 'เพิ่มข้อมูลสำเร็จแล้ว'),
                    onOk() { init(); }
                });
            } else {
                Modal.error({
                    ...getConfigModal('ผิดพลาด', operation === 'UPDATE' ? 'แก้ไขข้อมูลไม่สำเร็จ' : 'เพิ่มข้อมูลไม่สำเร็จ'),
                });
            }
        } else {
            Modal.warning({
                ...getConfigModal('แจ้งเตือน', 'กรุณากรอกชื่อหน่วยสินค้า'),
            });
        }
    };

    const onDelete = async (key_id: number, count?: number) => {
        if (count && count > 0) {
            Modal.warning({
                ...getConfigModal('ไม่สามารถลบได้', `หน่วยสินค้านี้มีสินค้าผูกอยู่ ${count} รายการ กรุณาย้ายหรือเปลี่ยนหน่วยสินค้าก่อน`),
            });
            return;
        }

        Modal.confirm({
            ...getConfigModal('ยืนยันการลบ', 'คุณต้องการลบหน่วยสินค้านี้หรือไม่ ?'),
            async onOk() {
                try {
                    let res = await api_product_unit('DELETE', {}, key_id);
                    if (res === 1) {
                        Modal.success({
                            ...getConfigModal('สำเร็จ', 'ลบข้อมูลสำเร็จแล้ว'),
                            onOk() { init(); }
                        });
                    } else {
                        Modal.error({
                            ...getConfigModal('ผิดพลาด', 'ลบข้อมูลไม่สำเร็จ'),
                        });
                    }
                } catch (err: any) {
                    // Backend answers a record that is already gone with a
                    // specific 404 and a sentence written for the user. Showing
                    // only "ลบข้อมูลไม่สำเร็จ" threw that away and left them
                    // guessing whether it was the network, their permissions, or
                    // a colleague who deleted it first.
                    Modal.error({
                        ...getConfigModal('ผิดพลาด', deleteFailureMessage(err, 'ลบข้อมูลไม่สำเร็จ')),
                    });
                }
            }
        });
    };

    const onDataDetailChange = (payload: any) => {
        setFormData(payload);
    };

    useEffect(() => {
        init();
    }, []);

    const totalActive = data.filter(item => item.status === 1).length;

    return (
        <>
            <div className='pageHeaderBanner'>
                <div className='pageHeaderTitle'>
                    <h2>⚖️ จัดการหน่วยสินค้า (Product Units)</h2>
                    <span className='pageHeaderSubtitle'>กำหนดหน่วยนับของสินค้าสำหรับใช้ในการขาย การนับสต็อก และการรับเข้าสินค้า</span>
                </div>
                <div className='headerStatsGrid'>
                    <div className='headerStatCard'>
                        <span className='statLabel'>หน่วยนับทั้งหมด</span>
                        <span className='statValue'>{data.length} หน่วย</span>
                    </div>
                    <div className='headerStatCard highlight'>
                        <span className='statLabel'>ใช้งานอยู่</span>
                        <span className='statValue highlight'>{totalActive} หน่วย</span>
                    </div>
                </div>
            </div>

            <CommonTableList 
                page="product_unit"
                placeholder={placeholder_txt} 
                columns={columns} 
                data={[...data]} 
                filterField={filterField} 
                onAdd={() => { onShowDetail('ADD'); }} 
            />

            <CommonDetail 
                width='460px'
                mode={mode} 
                title={title} 
                open={open} 
                onClosed={onCloseDetail} 
                onSaved={onSave}
                element={
                    <ProductUnitDetail 
                        disabled={mode === 'VIEW'}
                        productUnit={{...formData}} 
                        onChange={onDataDetailChange} 
                    />
                }
            />
        </>
    );
};

export default ProductUnit;
